import { useMemo, useState } from 'react'
import { db } from '../lib/firebase'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { where, setDoc, doc } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection, useShowsForTours } from '../lib/firestore'
import {
  useArtist,
  useArtists,
  usePeople,
  archiveArtist,
  setBandMembers,
  setTouringParty,
  updateArtist,
} from '../lib/artists'
import { formatDateRange, formatDayLong } from '../lib/format'
import type { BandMember, PartyMember, Person, Show, Tour, WithId } from '../types/models'
import TourHero from '../components/TourHero'
import SplitPane from '../components/SplitPane'
import Poster from '../components/Poster'
import TourGrid from '../components/TourGrid'
import Empty from '../components/Empty'
import Icon from '../components/Icon'
import BackfillDialog from '../components/BackfillDialog'
import DeleteConfirm from '../components/DeleteConfirm'
import { useIsMobile } from '../lib/useMediaQuery'
import MobileHeader from '../components/mobile/MobileHeader'
import { ArtistDialog } from './Artists'
import PersonDialog from '../dialogs/PersonDialog'

/* ============================================================================
   One artist.

   Everything that survives between tours: who normally travels, and what has
   been played. A tour is temporary; this is the permanent record it hangs off.

   The touring party is the part that earns its keep on day one — a new tour
   seeds its crew from here, so entering someone once removes them from every
   future setup.
   ============================================================================ */

export default function ArtistDetail() {
  const { artistId } = useParams<{ artistId: string }>()
  const { user, uid } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { artist, loading } = useArtist(artistId)
  const { orgId } = useArtists()
  const [backfilling, setBackfilling] = useState(false)
  const people = usePeople(orgId)

  const [editing, setEditing] = useState(false)
  const [addingPerson, setAddingPerson] = useState(false)
  const [addingBandMember, setAddingBandMember] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)

  const tours = useCollection<Tour>('tours', user ? [where('ownerId', '==', uid)] : [])
  const theirTours = useMemo(
    () =>
      tours.data
        .filter((tour) => tour.artistId === artistId)
        .sort((a, b) => b.startDate.toMillis() - a.startDate.toMillis()),
    [tours.data, artistId],
  )

  // Scoped to this artist's tours. An unconstrained read of `shows` is refused
  // by the rules, which is why this page used to report zero of everything.
  const tourIds = useMemo(() => theirTours.map((tour) => tour.id), [theirTours])
  const shows = useShowsForTours<Show>(tourIds)

  if (loading) return <div style={{ padding: 30 }} />

  if (!artist) {
    return (
      <div style={{ padding: 30 }}>
        <Empty title="Artist not found" message="It may have been archived." />
      </div>
    )
  }

  const party = artist.defaultTouringParty ?? []
  const band = artist.members ?? []
  // Documents written before bands existed have no `kind`. Read as solo.
  const isBand = (artist.kind ?? 'solo') === 'band'

  const theirShows = shows.data
  const cities = new Set(theirShows.map((show) => show.city).filter(Boolean))
  const countries = new Set(theirShows.map((show) => show.country).filter(Boolean))

  const nowMs = Date.now()

  // A tour that has finished. Backfill fills this list with a whole career, so
  // it is split out rather than left to bury the two runs that are live.
  const pastTours = theirTours.filter((tour) => tour.endDate.toMillis() < nowMs)
  const liveTours = theirTours.filter((tour) => tour.endDate.toMillis() >= nowMs)

  // Only days actually travelled. Counting a tour announced for 2027 as time
  // already spent on the road made the number meaningless the moment anything
  // future was on the books.
  const daysOnRoad = theirTours.reduce((total, tour) => {
    const start = tour.startDate.toMillis()
    const end = Math.min(tour.endDate.toMillis(), nowMs)
    if (end < start) return total
    return total + Math.max(1, Math.round((end - start) / 86_400_000) + 1)
  }, 0)

  const showsPlayed = theirShows.filter((show) => show.date.toMillis() <= nowMs).length

  const today = new Date()
  const onTour = theirTours.find(
    (tour) => tour.startDate.toDate() <= today && tour.endDate.toDate() >= today,
  )

  const nextShow = theirShows
    .filter((show) => show.date.toDate() >= new Date())
    .sort((a, b) => a.date.toMillis() - b.date.toMillis())[0]

  // Where this act actually plays. Derived from what has been booked rather
  // than typed in anywhere — it is the closest thing to market history the
  // data supports today.
  const topCities = Object.entries(
    theirShows.reduce<Record<string, number>>((counts, show) => {
      if (show.city) counts[show.city] = (counts[show.city] ?? 0) + 1
      return counts
    }, {}),
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)

  const primary = (
    <div className="stack" style={{ gap: 26, paddingRight: isMobile ? 0 : 18 }}>
      <section className="stack" style={{ gap: 10 }}>
        <div className="spread" style={{ alignItems: 'baseline' }}>
          <h2 className="t-eyebrow">Next date</h2>
          {/* Starts the flow with this artist already chosen — coming from
              their page, being asked which artist it's for is a silly
              question. */}
          <div className="row" style={{ gap: 8 }}>
            {/* History first, because an artist page with no past is the
                emptiest screen in the product. */}
            <button className="btn btn-secondary" onClick={() => setBackfilling(true)}>
              Backfill tours
            </button>
            <Link className="btn btn-primary" to={`/tours/new?artist=${artist.id}`}>
              New tour
            </Link>
          </div>
        </div>
        {nextShow ? (
          <Link
            to={`/tours/${nextShow.tourId}/shows/${nextShow.id}`}
            className="panel panel-signalled"
            style={{ display: 'block', padding: 20 }}
          >
            <div className="spread" style={{ gap: 16, alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                <span className="t-display-m truncate">{nextShow.city}</span>
                <span className="t-caption truncate">{nextShow.venueName}</span>
                <span className="t-spec-caption faint">
                  {formatDayLong(nextShow.date, nextShow.timezone)}
                </span>
              </div>
              <Icon name="chevronRight" size={18} style={{ color: 'var(--text-tertiary)' }} />
            </div>
          </Link>
        ) : (
          <p className="panel panel-pad t-caption">Nothing booked for this artist.</p>
        )}
      </section>

      {/* --------------------------------------------------------- tours */}
      <section className="stack" style={{ gap: 10 }}>
        <h2 className="t-eyebrow">Tours</h2>
        {liveTours.length === 0 ? (
          <p className="panel panel-pad t-caption">
            {pastTours.length > 0 ? 'Nothing booked. Past runs are below.' : 'No tours on this artist yet.'}
          </p>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {liveTours.map((tour) => (
              <Link
                key={tour.id}
                to={`/tours/${tour.id}`}
                className="panel row"
                style={{ gap: 14, padding: 14 }}
              >
                <Poster url={tour.posterURL} width={48} />
                <span className="grow stack" style={{ gap: 3, minWidth: 0 }}>
                  <span className="truncate" style={{ fontWeight: 600 }}>
                    {tour.tourName}
                  </span>
                  <span className="t-spec-caption dim">
                    {formatDateRange(tour.startDate, tour.endDate)}
                  </span>
                </span>
                <Icon name="chevronRight" size={16} style={{ color: 'var(--text-tertiary)' }} />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )

  const rail = (
    <div className="stack" style={{ gap: 26, paddingLeft: isMobile ? 0 : 18 }}>
      {/* ---------------------------------------------------------- band */}
      <section className="stack" style={{ gap: 10 }}>
        <div className="spread" style={{ alignItems: 'baseline' }}>
          <h2 className="t-eyebrow">Act</h2>
          <div className="row" style={{ gap: 6 }}>
            {(['solo', 'band'] as const).map((option) => {
              const on = (artist.kind ?? 'solo') === option
              return (
                <button
                  key={option}
                  type="button"
                  className="chip"
                  aria-pressed={on}
                  onClick={() => void updateArtist(artist.id, { name: artist.name, kind: option })}
                  style={{
                    cursor: 'pointer',
                    background: on ? 'var(--accent-wash)' : 'transparent',
                    border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                    color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
                  }}
                >
                  {option === 'solo' ? 'Solo artist' : 'Band'}
                </button>
              )
            })}
          </div>
        </div>

        {isBand && (
          <>
            <p className="t-caption" style={{ maxWidth: '64ch' }}>
              Who is in the band. They're saved as people, so a passport entered here is the
              same one the visa check reads on an international date.
            </p>

            {band.length === 0 ? (
              <p className="panel panel-pad t-caption">Nobody in the band yet.</p>
            ) : (
              <div className="panel rows">
                {band.map((member) => (
                  <BandRow
                    key={member.personId}
                    member={member}
                    person={people.find((entry) => entry.id === member.personId)}
                    onInstrument={(instrument) =>
                      void setBandMembers(
                        artist.id,
                        band.map((entry) =>
                          entry.personId === member.personId ? { ...entry, instrument } : entry,
                        ),
                      )
                    }
                    onRemove={() =>
                      void setBandMembers(
                        artist.id,
                        band.filter((entry) => entry.personId !== member.personId),
                      )
                    }
                  />
                ))}
              </div>
            )}

            <PeoplePicker
              people={people.filter(
                (person) => !band.some((entry) => entry.personId === person.id),
              )}
              label="Add from your people"
              onPick={(personId) =>
                void setBandMembers(artist.id, [...band, { personId, instrument: '' }])
              }
            />

            <button
              className="btn btn-quiet"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setAddingBandMember(true)}
            >
              <Icon name="plus" size={14} /> Add someone new
            </button>
          </>
        )}
      </section>

      {/* ------------------------------------------------- touring party */}
      <section className="stack" style={{ gap: 10 }}>
        <div className="spread" style={{ alignItems: 'baseline' }}>
          <h2 className="t-eyebrow">Touring party</h2>
          <button className="btn btn-quiet" onClick={() => setAddingPerson(true)}>
            <Icon name="plus" size={14} /> Add someone
          </button>
        </div>

        {party.length === 0 ? (
          <p className="panel panel-pad t-caption">
            Nobody yet. Add the people who come on every run and you stop re-entering them.
          </p>
        ) : (
          <div className="panel rows">
            {party.map((member) => (
              <PartyRow
                key={member.personId}
                member={member}
                person={people.find((entry) => entry.id === member.personId)}
                onToggleDefault={() =>
                  void setTouringParty(
                    artist.id,
                    party.map((entry) =>
                      entry.personId === member.personId
                        ? { ...entry, defaultIncluded: !entry.defaultIncluded }
                        : entry,
                    ),
                  )
                }
                onRemove={() =>
                  void setTouringParty(
                    artist.id,
                    party.filter((entry) => entry.personId !== member.personId),
                  )
                }
              />
            ))}
          </div>
        )}

        <PeoplePicker
          people={people.filter((person) => !party.some((entry) => entry.personId === person.id))}
          label="Add from your people"
          onPick={(personId) =>
            void setTouringParty(artist.id, [
              ...party,
              { personId, role: '', defaultIncluded: true },
            ])
          }
        />

      </section>


      {topCities.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <h2 className="t-eyebrow">Markets played</h2>
          <div className="panel rows">
            {topCities.map(([city, count]) => (
              <div key={city} className="spread" style={{ padding: '10px 16px' }}>
                <span className="truncate">{city}</span>
                <span className="t-spec-caption faint">
                  {count} show{count === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )

  const body = isMobile ? (
    <>
      {primary}
      {rail}
    </>
  ) : (
    <SplitPane
      storageKey="encore.artist.railWidth"
      defaultRailWidth={420}
      minRailWidth={300}
      primary={primary}
      rail={rail}
    />
  )

  return (
    <>
      <BackfillDialog
        open={backfilling}
        onClose={() => setBackfilling(false)}
        artistId={artist.id}
        artistName={artist.name}
      />

      {isMobile && <MobileHeader title={artist.name} />}

      {/* The artwork runs to the window edge, so the hero sits outside the
          padded column — an inset banner with a hairline reads as a widget,
          and this page is the permanent record everything else hangs off. */}
      <TourHero
        artist={onTour ? 'ON THE ROAD' : 'ARTIST'}
        tourName={artist.name}
        posterURL={artist.avatarURL}
        canEdit
        editLabel="Edit artist"
        onEdit={() => setEditing(true)}
        // The desktop set. The phone keeps its own, because a wide letterbox
        // and a nearly-square hero cannot share one crop.
        framing={{
          scale: artist.avatarScale ?? 1,
          offsetX: artist.avatarOffsetX ?? 0,
          offsetY: artist.avatarOffsetY ?? 0,
        }}
        onSaveFraming={(framing) =>
          setDoc(
            doc(db, 'artists', artist.id),
            {
              avatarScale: framing.scale,
              avatarOffsetX: framing.offsetX,
              avatarOffsetY: framing.offsetY,
            },
            { merge: true },
          )
        }
        footer={
          <div className="row" style={{ gap: 22, flexWrap: 'wrap' }}>
            <HeroStat value={theirTours.length} label={theirTours.length === 1 ? 'Tour' : 'Tours'} />
            {/* Played, not booked. A career total that quietly includes next
                year's stadium run is a vanity number. */}
            <HeroStat value={showsPlayed} label={showsPlayed === 1 ? 'Show' : 'Shows'} />
            <HeroStat value={cities.size} label={cities.size === 1 ? 'City' : 'Cities'} />
            {countries.size > 1 && <HeroStat value={countries.size} label="Countries" />}
            <HeroStat value={daysOnRoad} label="Days on the road" />
            <HeroStat value={party.length} label="Touring party" />
          </div>
        }
      />

      <div
        className="sections"
        style={{
          maxWidth: 'var(--content-max)',
          margin: '0 auto',
          padding: isMobile ? '20px var(--gutter) 120px' : '28px var(--gutter) 80px',
          gap: 30,
        }}
      >
        {body}
      </div>

      <PastTours tours={pastTours} isMobile={isMobile} />

      <div
        className="sections"
        style={{
          maxWidth: 'var(--content-max)',
          margin: '0 auto',
          padding: isMobile ? '0 var(--gutter) 120px' : '0 var(--gutter) 80px',
        }}
      >
        <button
          className="btn btn-quiet"
          style={{ alignSelf: 'flex-start', color: 'var(--destructive)' }}
          onClick={() => setConfirmArchive(true)}
        >
          Archive {artist.name}
        </button>
      </div>

      {editing && orgId && (
        <ArtistDialog orgId={orgId} artist={artist} onClose={() => setEditing(false)} />
      )}

      {addingPerson && orgId && (
        <PersonDialog
          orgId={orgId}
          onClose={() => setAddingPerson(false)}
          onCreated={(personId) =>
            void setTouringParty(artist.id, [
              ...party,
              { personId, role: '', defaultIncluded: true },
            ])
          }
        />
      )}

      {addingBandMember && orgId && (
        <PersonDialog
          orgId={orgId}
          onClose={() => setAddingBandMember(false)}
          onCreated={(personId) =>
            void setBandMembers(artist.id, [...band, { personId, instrument: '' }])
          }
        />
      )}

      {confirmArchive && (
        <DeleteConfirm
          label={artist.name}
          detail="Archiving hides them from the roster. Their tours stay exactly as they are — an artist is never deleted, because a tour points at one and a dangling reference loses the tour's history rather than the artist's."
          onCancel={() => setConfirmArchive(false)}
          onConfirm={async () => {
            await archiveArtist(artist.id)
            navigate('/artists', { replace: true })
          }}
        />
      )}
    </>
  )
}

/**
 * A figure in the masthead.
 *
 * Sits on artwork, so the colours are literal rather than tokens — a theme
 * token would go dark-on-dark over the scrim.
 */
function HeroStat({ value, label }: { value: number; label: string }) {
  return (
    <div className="stack" style={{ gap: 1 }}>
      <span
        className="t-timecode"
        style={{ fontSize: '1.5rem', color: '#fff', textShadow: '0 2px 12px rgb(0 0 0 / 0.5)' }}
      >
        {value.toLocaleString()}
      </span>
      <span
        className="t-eyebrow"
        style={{ color: 'rgb(255 255 255 / 0.72)' }}
      >
        {label}
      </span>
    </div>
  )
}

function PartyRow({
  member,
  person,
  onToggleDefault,
  onRemove,
}: {
  member: PartyMember
  person?: WithId<Person>
  onToggleDefault: () => void
  onRemove: () => void
}) {
  return (
    <div className="row" style={{ padding: '12px 16px', gap: 12 }}>
      <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="truncate" style={{ fontWeight: 600 }}>
          {person?.name ?? 'Removed person'}
        </span>
        <span className="t-caption truncate">
          {member.role || person?.email || 'No role set'}
        </span>
      </span>
      <button
        type="button"
        className="chip"
        aria-pressed={member.defaultIncluded}
        onClick={onToggleDefault}
        title="Include on new tours by default"
        style={{
          cursor: 'pointer',
          background: member.defaultIncluded ? 'var(--accent-wash)' : 'transparent',
          border: `var(--hairline-width) solid ${
            member.defaultIncluded ? 'var(--accent)' : 'var(--hairline)'
          }`,
          color: member.defaultIncluded ? 'var(--accent-text)' : 'var(--text-secondary)',
        }}
      >
        {member.defaultIncluded ? 'On new tours' : 'Not by default'}
      </button>
      <button
        type="button"
        aria-label={`Remove ${person?.name ?? 'this person'}`}
        onClick={onRemove}
        style={{ color: 'var(--text-tertiary)' }}
      >
        <Icon name="close" size={15} />
      </button>
    </div>
  )
}

function BandRow({
  member,
  person,
  onInstrument,
  onRemove,
}: {
  member: BandMember
  person?: WithId<Person>
  onInstrument: (instrument: string) => void
  onRemove: () => void
}) {
  return (
    <div className="row" style={{ padding: '10px 16px', gap: 12 }}>
      <span className="grow truncate" style={{ fontWeight: 600, minWidth: 0 }}>
        {person?.name ?? 'Removed person'}
      </span>
      <input
        className="field"
        style={{ width: 150 }}
        aria-label={`What ${person?.name ?? 'they'} play`}
        defaultValue={member.instrument}
        // On blur, not per keystroke — a write per character would be a write
        // per character.
        onBlur={(event) => {
          if (event.target.value !== member.instrument) onInstrument(event.target.value)
        }}
      />
      <button
        type="button"
        aria-label={`Remove ${person?.name ?? 'this member'}`}
        onClick={onRemove}
        style={{ color: 'var(--text-tertiary)' }}
      >
        <Icon name="close" size={15} />
      </button>
    </div>
  )
}

/** People in the organisation not already on this list. */
function PeoplePicker({
  people,
  label,
  onPick,
}: {
  people: Array<WithId<Person>>
  label: string
  onPick: (personId: string) => void
}) {
  if (people.length === 0) return null
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="t-eyebrow">{label}</span>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {people.map((person) => (
          <button
            key={person.id}
            type="button"
            className="chip"
            style={{
              cursor: 'pointer',
              border: 'var(--hairline-width) solid var(--hairline)',
            }}
            onClick={() => onPick(person.id)}
          >
            + {person.name}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ============================================================================
   Past tours

   A grid rather than the shelf this was. A rail hid most of a career behind a
   sideways gesture and dragged the page with it; a grid fills the width, grows
   downward, and can be read at a glance.
   ============================================================================ */

function PastTours({
  tours,
  isMobile,
}: {
  tours: Array<WithId<Tour>>
  isMobile: boolean
}) {
  if (tours.length === 0) return null

  return (
    <section
      className="stack"
      style={{
        gap: 14,
        maxWidth: 'var(--content-max)',
        width: '100%',
        margin: '0 auto',
        padding: isMobile ? '0 var(--gutter) 26px' : '0 var(--gutter) 40px',
      }}
    >
      <div className="spread" style={{ alignItems: 'baseline' }}>
        <h2 className="t-eyebrow">Past tours</h2>
        <span className="t-caption">{tours.length}</span>
      </div>
      <TourGrid tours={tours} />
    </section>
  )
}
